import React, { useState } from "react";
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Link,
  Divider,
  InputAdornment,
  IconButton,
  Alert,
  Fade,
  Grow,
  CircularProgress,
  Stack,
  useTheme,
  alpha,
  Avatar,
  Tooltip,
  Zoom,
  Chip,
  Card,
  CardContent,
  Modal,
  Backdrop,
} from "@mui/material";
import {
  Phone as PhoneIcon,
  ArrowForward as ArrowForwardIcon,
  Security as SecurityIcon,
  Apple as AppleIcon,
  Google as GoogleIcon,
  Facebook as FacebookIcon,
  WhatsApp as WhatsAppIcon,
  VerifiedUser as VerifiedUserIcon,
  Shield as ShieldIcon,
  CheckCircle as CheckCircleIcon,
  Smartphone as SmartphoneIcon,
  Close as CloseIcon,
  Key as KeyIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { createRoot } from "react-dom/client";
// import authService from '../../../services/api/authService'; // Adjust path as needed
import {
  SignIn,
  VerifyOTP,
  ResendOTP,
  RefreshToken,
} from "../../../services/api/authService";
import {
  setUser,
  setIsAuthenticated,
  setToken,
  setRefreshToken,
} from "../../../redux/slices/authSlice";
import { Provider, useDispatch } from "react-redux";
import { store } from "../../../redux/store";

// Styled components for advanced customization
const GradientContainer = styled(Container)(({ theme }) => ({
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: `radial-gradient(circle at 20% 50%, ${alpha(theme.palette.primary.light, 0.08)} 0%, ${alpha(theme.palette.background.default, 0.95)} 100%)`,
  padding: theme.spacing(2),
}));

const GlassCard = styled(Paper)(({ theme }) => ({
  borderRadius: theme.spacing(3),
  background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.95)} 0%, ${alpha(theme.palette.background.default, 0.98)} 100%)`,
  backdropFilter: "blur(10px)",
  boxShadow: `0 25px 45px -12px ${alpha(theme.palette.common.black, 0.25)}`,
  overflow: "hidden",
  position: "relative",
  transition: "transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: `0 35px 55px -15px ${alpha(theme.palette.common.black, 0.3)}`,
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: theme.spacing(2),
    transition: "all 0.2s ease",
    "&:hover": {
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.primary.main,
      },
    },
    "&.Mui-focused": {
      "& .MuiOutlinedInput-notchedOutline": {
        borderWidth: "2px",
      },
    },
  },
}));

const GradientButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  padding: theme.spacing(1.5, 3),
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  color: theme.palette.common.white,
  fontWeight: 600,
  fontSize: "1rem",
  textTransform: "none",
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "scale(1.02)",
    boxShadow: `0 8px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
  },
  "&:disabled": {
    background: theme.palette.grey[300],
    transform: "none",
  },
}));

const AnimatedBorder = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  height: "4px",
  background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`,
  backgroundSize: "200% 100%",
  animation: "gradientShift 3s ease infinite",
  "@keyframes gradientShift": {
    "0%": { backgroundPosition: "0% 50%" },
    "50%": { backgroundPosition: "100% 50%" },
    "100%": { backgroundPosition: "0% 50%" },
  },
}));

const CountryCodeSelector = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  padding: theme.spacing(0.5, 1.5),
  borderRadius: theme.spacing(2),
  //   backgroundColor: alpha(theme.palette.action.hover, 0.5),
  cursor: "pointer",
}));

// Modal styled component
const StyledModal = styled(Modal)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const SignInContent = ({ onClose, onSuccess }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [step, setStep] = useState("mobile"); // 'mobile' or 'otp'
  console.log("SignInContent rendered with step:", step);
  const [mobileNumber, setMobileNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [touched, setTouched] = useState(false);
  const [countryCode, setCountryCode] = useState("+91");
  const [otpRef, setOtpRef] = useState(null);
  const [resendTimer, setResendTimer] = useState(0);

  const validateMobileNumber = (number) => {
    const mobileRegex = /^[6-9]\d{9}$/;
    if (!number) return "Mobile number is required";
    if (!mobileRegex.test(number))
      return "Please enter a valid 10-digit mobile number";
    return "";
  };

  const handleMobileChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 10);
    setMobileNumber(value);
    if (touched) {
      setError(validateMobileNumber(value));
    }
  };

  const handleBlur = () => {
    setTouched(true);
    setError(validateMobileNumber(mobileNumber));
  };

  const startResendTimer = () => {
    const timer = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Send OTP
  const handleSendOTP = async (e) => {
    e.preventDefault();
    const validationError = validateMobileNumber(mobileNumber);
    setError(validationError);
    setTouched(true);

    if (!validationError) {
      setIsLoading(true);
      setError("");

      try {
        // Call your actual API to send OTP
        const response = await SignIn(mobileNumber);

        console.log("OTP Response:", response.data); // Debugging line to check the response

        if (response.data.success) {
          setStep("otp");
          setOtpRef(response.otpRef);
          setResendTimer(30);
          startResendTimer();
        } else {
          setError(response.message);
        }
      } catch (err) {
        setError("Failed to send OTP. Please try again.");
      } finally {
        setIsLoading(false);
      }
    }
  };

  // In SignInContent component's handleVerifyOTP function
  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (!otp || otp.length !== 6) {
      setError("Please enter a valid 6-digit OTP");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const response = await VerifyOTP(mobileNumber, otp, otpRef);
      console.log("Verify OTP Response:", response.data);

      if (response.data.success) {
        setSuccess(true);

        // Extract user data and tokens
        const userData = response.data?.data?.customer;
        const accessToken = response.data?.data?.accessToken;
        const refreshToken = response.data?.data?.refreshToken; // IMPORTANT: Get refresh token
        window.location.reload(); // Force reload to ensure all components get updated auth state

        console.log("Access Token received:", accessToken);
        console.log("Refresh Token received:", refreshToken);

        if (!accessToken) {
          throw new Error("No access token received from server");
        }

        // Store in Redux
        dispatch(setUser(userData));
        dispatch(setToken(accessToken));
        if (refreshToken) {
          dispatch(setRefreshToken(refreshToken)); // Store refresh token
        }
        dispatch(setIsAuthenticated(true));

        // Also store in localStorage as backup
        localStorage.setItem("authToken", accessToken);
        if (refreshToken) {
          localStorage.setItem("refreshToken", refreshToken); // Store refresh token
        }
        localStorage.setItem("user", JSON.stringify(userData));
        localStorage.setItem("isAuthenticated", "true");
        localStorage.setItem("userName", userData?.name || "");

        // Call onSuccess callback
        if (onSuccess) {
          onSuccess({
            countryCode,
            mobileNumber,
            user: userData?.name,
            token: accessToken,
            refreshToken: refreshToken,
            userData: userData,
          });
        }

        // Close modal after success
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        setError(response.data.message || "Verification failed");
      }
    } catch (err) {
      console.error("Verification error:", err);
      setError(err.message || "Failed to verify OTP. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Resend OTP
  const handleResendOTP = async () => {
    if (resendTimer > 0) return;

    setIsLoading(true);
    setError("");

    try {
      const response = await ResendOTP(mobileNumber);

      if (response.success) {
        setResendTimer(30);
        startResendTimer();
        setOtpRef(response.otpRef);
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError("Failed to resend OTP. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <GradientContainer maxWidth={false}>
      <Grow in timeout={800}>
        <Box
          sx={{
            width: "100%",
            maxWidth: 520,
            mx: "auto",
            position: "relative",
          }}
        >
          {/* Close Button */}
          <IconButton
            onClick={onClose}
            sx={{
              position: "absolute",
              right: 16,
              top: 16,
              zIndex: 1,
              backgroundColor: alpha(theme.palette.common.white, 0.9),
              "&:hover": {
                backgroundColor: alpha(theme.palette.common.white, 1),
              },
            }}
          >
            <CloseIcon />
          </IconButton>

          <GlassCard elevation={0}>
            <AnimatedBorder />

            <CardContent sx={{ p: { xs: 3, sm: 5 } }}>
              {/* Header Section */}
              <Box textAlign="center" mb={4}>
                <Zoom in timeout={500}>
                  <Avatar
                    sx={{
                      width: 64,
                      height: 64,
                      mx: "auto",
                      mb: 2,
                      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                      boxShadow: `0 8px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
                    }}
                  >
                    {step === "mobile" ? (
                      <SmartphoneIcon sx={{ fontSize: 32 }} />
                    ) : (
                      <KeyIcon sx={{ fontSize: 32 }} />
                    )}
                  </Avatar>
                </Zoom>

                <Typography
                  variant="h4"
                  component="h1"
                  fontWeight={700}
                  sx={{
                    background: `linear-gradient(135deg, #181616, ${theme.palette.primary.main})`,
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    mb: 1,
                  }}
                >
                  {step === "mobile" ? "Almost there!" : "Verify OTP"}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {step === "mobile"
                    ? "Simply sign in to place your order"
                    : `We've sent a verification code to +91 ${mobileNumber}`}
                </Typography>
              </Box>

              {/* Success Alert */}
              <Fade in={success}>
                <Alert
                  icon={<CheckCircleIcon fontSize="inherit" />}
                  severity="success"
                  sx={{
                    mb: 3,
                    borderRadius: 2,
                  }}
                >
                  Sign in successful! Redirecting...
                </Alert>
              </Fade>

              {/* Error Alert */}
              {error && (
                <Alert
                  severity="error"
                  sx={{ mb: 3, borderRadius: 2 }}
                  onClose={() => setError("")}
                >
                  {error}
                </Alert>
              )}

              {/* Sign In Form */}
              <form
                onSubmit={step === "mobile" ? handleSendOTP : handleVerifyOTP}
              >
                <Stack spacing={3}>
                  {step === "mobile" ? (
                    // Mobile Number Field
                    <Box>
                      <Typography
                        variant="body2"
                        fontWeight={500}
                        mb={1}
                        color="text.secondary"
                      >
                        Mobile Number
                      </Typography>
                      <StyledTextField
                        fullWidth
                        placeholder="Enter your mobile number"
                        value={mobileNumber}
                        onChange={handleMobileChange}
                        onBlur={handleBlur}
                        error={!!error && touched}
                        helperText={touched && error}
                        disabled={isLoading}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <CountryCodeSelector
                                onClick={() => console.log("Change country")}
                              >
                                {/* <PhoneIcon fontSize="small" color="primary" /> */}
                                <Typography variant="body2" fontWeight={500}>
                                  {countryCode}
                                </Typography>
                              </CountryCodeSelector>
                            </InputAdornment>
                          ),
                          endAdornment: mobileNumber && !error && touched && (
                            <InputAdornment position="end">
                              <Tooltip title="Valid number">
                                <CheckCircleIcon
                                  color="success"
                                  sx={{ fontSize: 20 }}
                                />
                              </Tooltip>
                            </InputAdornment>
                          ),
                        }}
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 1, display: "block" }}
                      >
                        We'll send you a verification OTP
                      </Typography>
                    </Box>
                  ) : (
                    // OTP Field
                    <Box>
                      <Typography
                        variant="body2"
                        fontWeight={500}
                        mb={1}
                        color="text.secondary"
                      >
                        Enter OTP
                      </Typography>
                      <StyledTextField
                        fullWidth
                        placeholder="Enter 6-digit OTP"
                        value={otp}
                        onChange={(e) =>
                          setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
                        }
                        disabled={isLoading}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <KeyIcon color="primary" />
                            </InputAdornment>
                          ),
                        }}
                      />
                      <Box
                        sx={{
                          mt: 1,
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          Didn't receive the code?
                        </Typography>
                        <Button
                          size="small"
                          onClick={handleResendOTP}
                          disabled={resendTimer > 0 || isLoading}
                          sx={{ textTransform: "none" }}
                        >
                          {resendTimer > 0
                            ? `Resend in ${resendTimer}s`
                            : "Resend OTP"}
                        </Button>
                      </Box>
                    </Box>
                  )}

                  {/* Action Button */}
                  <GradientButton
                    type="submit"
                    fullWidth
                    disabled={
                      isLoading ||
                      (step === "mobile" && (!mobileNumber || !!error)) ||
                      (step === "otp" && (!otp || otp.length !== 6))
                    }
                    endIcon={!isLoading && <ArrowForwardIcon />}
                  >
                    {isLoading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : step === "mobile" ? (
                      "Send OTP"
                    ) : (
                      "Verify & Sign In"
                    )}
                  </GradientButton>

                  {/* Back to mobile button (only show in OTP step) */}
                  {step === "otp" && (
                    <Button
                      onClick={() => {
                        setStep("mobile");
                        setOtp("");
                        setError("");
                      }}
                      sx={{ textTransform: "none" }}
                    >
                      ← Back to mobile number
                    </Button>
                  )}

                  {/* Terms and Privacy (only show in mobile step) */}
                  {step === "mobile" && (
                    <>
                      {/* Trust Badge */}
                      <Box
                        sx={{
                          mt: 2,
                          pt: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 2,
                          flexWrap: "wrap",
                        }}
                      ></Box>
                    </>
                  )}
                </Stack>
              </form>
            </CardContent>
          </GlassCard>
        </Box>
      </Grow>
    </GradientContainer>
  );
};

// Create a singleton instance for the modal
let modalRoot = null;
let rootInstance = null;

const openSignInModal = (options = {}) => {
  const { onSuccess, onClose } = options;

  // Clean up existing modal if any
  if (modalRoot && modalRoot.parentNode) {
    if (rootInstance) {
      rootInstance.unmount();
    }
    modalRoot.parentNode.removeChild(modalRoot);
  }

  // Create modal container
  modalRoot = document.createElement("div");
  modalRoot.id = "signin-modal-root";
  document.body.appendChild(modalRoot);

  // Function to close and cleanup
  const handleClose = () => {
    if (rootInstance) {
      rootInstance.unmount();
    }
    if (modalRoot && modalRoot.parentNode) {
      modalRoot.parentNode.removeChild(modalRoot);
    }
    modalRoot = null;
    rootInstance = null;
    if (onClose) onClose();
  };

  // Create root and render
  rootInstance = createRoot(modalRoot);
  rootInstance.render(
    <React.StrictMode>
      <Provider store={store}>
        <StyledModal
          open={true}
          onClose={handleClose}
          closeAfterTransition
          slots={{ backdrop: Backdrop }}
          slotProps={{
            backdrop: {
              timeout: 500,
              sx: {
                backgroundColor: "rgba(0, 0, 0, 0.6)",
                backdropFilter: "blur(4px)",
              },
            },
          }}
        >
          <SignInContent
            onClose={handleClose}
            onSuccess={(data) => {
              if (onSuccess) onSuccess(data);
              console.log("SignIn successful with data:", data); // Debugging line to check the success data
            }}
          />
        </StyledModal>
      </Provider>
    </React.StrictMode>,
  );
};

// Export both as default and named export for flexibility
const SignInLayout = {
  open: openSignInModal,
};

// Default export
export default SignInLayout;

// Named export for direct import
export { openSignInModal as openSignIn };
